import { FormEvent, useMemo, useState } from "react";
import { resetPassword } from "../api/adminAuthApi";
import { AuthPasswordField } from "../components/AuthPasswordField";
import { AuthShell } from "../components/AuthShell";
import { AuthStatusMessage } from "../components/AuthStatusMessage";
import { AuthSubmitButton } from "../components/AuthSubmitButton";
import { navigateTo } from "../components/authNavigation";

function getFriendlyResetPasswordError(
  error: { code?: string | null; message?: string } | null,
): string {
  if (!error) {
    return "Unable to reset your password.";
  }

  if (
    error.code === "INVALID_TOKEN" ||
    /invalid token/i.test(error.message ?? "")
  ) {
    return "This reset link has expired or is no longer valid. Request a new reset link to continue.";
  }

  return error.message ?? "Unable to reset your password.";
}

function shouldShowResetLinkRecovery(
  error: { code?: string | null; message?: string } | null,
): boolean {
  if (!error) {
    return false;
  }

  return (
    error.code === "INVALID_TOKEN" || /invalid token/i.test(error.message ?? "")
  );
}

export function ResetPasswordPage() {
  const token = useMemo(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("token") ?? "";
  }, []);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRequestNewLink, setShowRequestNewLink] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setFieldError(null);
    setSuccess(null);
    setShowRequestNewLink(false);

    if (!token) {
      setError("This password reset link is missing its token.");
      setFieldError("This password reset link is missing its token.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Your new passwords need to match.");
      setFieldError("Your new passwords need to match.");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await resetPassword({
        newPassword,
        token,
      });

      if (!result.ok) {
        setError(getFriendlyResetPasswordError(result.error));
        setFieldError(result.error.message ?? "Unable to reset your password.");
        setShowRequestNewLink(shouldShowResetLinkRecovery(result.error));
        return;
      }

      setSuccess("Password updated. Redirecting you back to sign in...");
      navigateTo("/admin/sign-in");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthShell
      title="Create a New Workspace Password"
      subtitle="Enter and confirm your new password to regain access to your workspace."
      badgeIcon="lock_reset"
      badgeLabel="Metaphysical Labs"
      data-oid="haubdkv"
    >
      <form className="auth-form" onSubmit={handleSubmit} data-oid="lex-7i9">
        <AuthPasswordField
          label="New Password"
          name="newPassword"
          autoComplete="new-password"
          placeholder="Enter your new password"
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          data-oid="osle9nm"
        />

        <AuthPasswordField
          label="Confirm Password"
          name="confirmPassword"
          autoComplete="new-password"
          placeholder="Confirm your new password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          error={fieldError}
          data-oid="hgvdt4d"
        />

        <div className="auth-guidance" data-oid="xoik1:f">
          <p className="auth-guidance__title" data-oid="p3yy_6i">
            Password Requirements
          </p>
          <ul className="auth-guidance__list" data-oid="b_cx1:r">
            <li data-oid="7n1i.ot">At least 8 characters</li>
            <li data-oid="t8zuulz">At least 1 uppercase letter</li>
            <li data-oid="93xu:4u">At least 1 number or symbol</li>
          </ul>
        </div>
        {error ? (
          <AuthStatusMessage tone="error" message={error} data-oid="efpo-0n" />
        ) : null}
        {success ? (
          <AuthStatusMessage
            tone="success"
            message={success}
            data-oid="n7162u1"
          />
        ) : null}
        {showRequestNewLink ? (
          <div
            className="auth-inline-row auth-inline-row--start"
            data-oid="p_v86:y"
          >
            <button
              className="auth-link-button"
              type="button"
              onClick={() => navigateTo("/admin/forgot-password")}
              data-oid="tv5_i29"
            >
              Request a New Reset Link
            </button>
          </div>
        ) : null}
        <AuthSubmitButton
          type="submit"
          isBusy={isSubmitting}
          busyLabel="Updating Password..."
          data-oid="p7r3fpd"
        >
          Update Password
        </AuthSubmitButton>
      </form>
      <p className="auth-footer-copy" data-oid="auyhcf.">
        Back to{" "}
        <button
          className="auth-link-button"
          type="button"
          onClick={() => navigateTo("/admin/sign-in")}
          data-oid="h23lv2r"
        >
          Sign In
        </button>
      </p>
    </AuthShell>
  );
}
